/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import StudentDetailModal from './StudentDetailModal';
import PDFExporter from './PDFExporter';
import SearchableDropdown from './SearchableDropdown';
import './PreviousExamResults.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

/** Sınıf listesi sınav sütunu: yükleme bitmeden −; eksik/0 → 0.0; değer yüzde ölçeğinde ama % işareti yok (direkt puan). */
function formatRosterExamScoreDisplay(score, scoresReady) {
    if (!scoresReady) return '-';
    const n = score == null || score === '' ? NaN : Number(score);
    const v = Number.isFinite(n) ? n : 0;
    if (v <= 0) return '0';
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const PreviousExamResults = () => {
    const { t } = useTranslation();
    const safeText = useCallback((value) => {
        if (value === null || value === undefined) return '';
        const text = String(value).trim();
        if (!text) return '';
        const lowered = text.toLowerCase();
        if (lowered === 'undefined' || lowered === 'null' || lowered === 'none') return '';
        return text;
    }, []);

    const formatCourseLabel = useCallback((course) => {
        const courseCode = course?.courseCode === null || course?.courseCode === undefined
            ? ''
            : String(course.courseCode).trim();
        const courseName = safeText(course?.courseName);
        const programName = safeText(course?.programName);
        const facultyName = safeText(course?.facultyName);

        const leftPart = [courseCode, courseName].filter(Boolean).join(' - ');
        const rightPart = [programName, facultyName].filter(Boolean).join(' - ');
        if (leftPart && rightPart) return `${leftPart} (${rightPart})`;
        return leftPart || rightPart || 'İsimsiz Ders';
    }, [safeText]);

    // eslint-disable-next-line no-unused-vars
    const [educationTypes, setEducationTypes] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [faculties, setFaculties] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [programs, setPrograms] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [courses, setCourses] = useState([]);
    const [assignedCourses, setAssignedCourses] = useState([]);
    // Component unmount cleanup (Navigation için kritik)
    useEffect(() => {
        return () => {
            setLoading(false);
        };
    }, []);
    const uniqueAssignedCourses = useMemo(() => {
        // Aynı ders birden fazla yetki/atama kaydıyla gelirse seçimde tek satır göster.
        const byCourseId = new Map();
        (assignedCourses || []).forEach((course) => {
            const id = String(course.courseId || '');
            if (id && !byCourseId.has(id)) byCourseId.set(id, course);
        });
        const list = [...byCourseId.values()];
        // Sıralama: Fakülte > Program > Ders adı
        return list.sort((a, b) => (
            safeText(a.facultyName).localeCompare(safeText(b.facultyName)) ||
            safeText(a.programName).localeCompare(safeText(b.programName)) ||
            safeText(a.courseName).localeCompare(safeText(b.courseName))
        ));
    }, [assignedCourses, safeText]);
    // eslint-disable-next-line no-unused-vars
    const [examTypes, setExamTypes] = useState([]);
    
    const [selectedEducationType, setSelectedEducationType] = useState('');
    const [selectedFaculty, setSelectedFaculty] = useState('');
    const [selectedProgram, setSelectedProgram] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [selectedExamType, setSelectedExamType] = useState('');
    
    const [results, setResults] = useState(null);
    const [commonClassAnalysis, setCommonClassAnalysis] = useState(null);
    const [studentScoresByKey, setStudentScoresByKey] = useState({}); // { key -> { canonType -> dc_percent } }
    const [scoresReady, setScoresReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [courseOutcomes, setCourseOutcomes] = useState([]);
    const [programOutcomes, setProgramOutcomes] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [examWeights, setExamWeights] = useState([]);
    const [outcomeMappings, setOutcomeMappings] = useState({});

    // Sınav türü kanonikleştirme ve etiketler
    const canonExamType = useCallback((value) => {
        const v = String(value || '').trim().toLowerCase();
        if (
            v === 'midterm' ||
            v.includes('vize') ||
            v.includes('ara sınav') ||
            v.includes('ara sinav') ||
            v.includes('arasinav')
        ) return 'vize';
        if (
            v.includes('final') ||
            v.includes('dönem sonu') ||
            v.includes('donem sonu') ||
            v.includes('donemsonu') ||
            v.includes('dönemsonu')
        ) return 'final';
        if (v.includes('but') || v.includes('bütün') || v.includes('butun') || v.includes('bütü') || v.includes('butu') || v.includes('resit')) return 'but';
        if (v.includes('quiz') || v.includes('kısa') || v.includes('kisa')) return 'quiz';
        if (v === 'pro') return 'pro';
        if (v.includes('ödev') || v.includes('odev')) return 'ödev';
        return v;
    }, []);

    const labelForExamType = useCallback((t) => {
        switch (t) {
            case 'vize': return 'Ara Sınav';
            case 'final': return 'Final';
            case 'but': return 'Bütünleme';
            case 'quiz': return 'Kısa Sınav';
            case 'pro': return 'Pro';
            case 'odev': return 'Ödev';
            case 'ödev': return 'Ödev';
            default: return `${String(t).charAt(0).toUpperCase()}${String(t).slice(1)}`;
        }
    }, []);

    // Sınıf listesi yükleme için state'ler
    const [classListFile, setClassListFile] = useState(null);
    const [isUploadingClassList, setIsUploadingClassList] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [uploadedFiles, setUploadedFiles] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [showClassUpload, setShowClassUpload] = useState(false);
    const [classRoster, setClassRoster] = useState([]);
    const [examAttempts, setExamAttempts] = useState([]);

    // Akademisyen izinli id'ler için state'ler
    const [allowedEducationTypes, setAllowedEducationTypes] = useState([]);
    const [allowedFaculties, setAllowedFaculties] = useState([]);
    const [allowedPrograms, setAllowedPrograms] = useState([]);
    const [allowedCourses, setAllowedCourses] = useState([]);

    // Akademisyen id'si: session veya localStorage
    const akademisyenId = sessionStorage.getItem('userRole') === 'akademisyen'
        ? sessionStorage.getItem('akademisyen_id') : null;

    // Satır seçimi ve analiz için sınav türü
    // eslint-disable-next-line no-unused-vars
    const [highlightedWeightIndex, setHighlightedWeightIndex] = useState(null);
    const [selectedExamTypeForAnalysis, setSelectedExamTypeForAnalysis] = useState('');

    // Öğrenci detay modalı için state'ler
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [isStudentDetailModalOpen, setIsStudentDetailModalOpen] = useState(false);

    // Öntest analiz modalı
    const [pretestModal, setPretestModal] = useState(null); // { student, attempt, questions }
    const [pretestModalLoading, setPretestModalLoading] = useState(false);

    // Animasyon durumları için state'ler
    // eslint-disable-next-line no-unused-vars
    const [showFaculty, setShowFaculty] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showProgram, setShowProgram] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showCourse, setShowCourse] = useState(false);

    // Referanslar oluştur
    const facultySelectRef = useRef(null);
    const programSelectRef = useRef(null);
    const courseSelectRef = useRef(null);
    // eslint-disable-next-line no-unused-vars
    const examWeightsRef = useRef(null);

    // Akademisyen izinleri + dersleri çek
    useEffect(() => {
        if (akademisyenId) {
            axios.get(`${API_ENDPOINTS.AKADEMISYEN_PERMISSIONS}?akademisyen_id=${akademisyenId}`)
                .then(res => {
                    setAllowedEducationTypes(res.data.education_types || []);
                    setAllowedFaculties(res.data.faculties || []);
                    setAllowedPrograms(res.data.programs || []);
                    setAllowedCourses(res.data.courses || []);
                })
                .catch(() => {
                    setAllowedEducationTypes([]);
                    setAllowedFaculties([]);
                    setAllowedPrograms([]);
                    setAllowedCourses([]);
                });

            // Ders İçerikleri ile aynı kaynak: /api/akademisyen/courses
            // (detay endpoint sadece ek alanları zenginleştirmek için kullanılır)
            Promise.allSettled([
                axios.get(`${API_ENDPOINTS.AKADEMISYEN_COURSES}?akademisyen_id=${akademisyenId}`),
                axios.get(`${API_ENDPOINTS.AKADEMISYEN_COURSES_DETAILED}?akademisyen_id=${akademisyenId}`)
            ])
                .then(([coursesResult, detailedResult]) => {
                    const courseRows = coursesResult.status === 'fulfilled' && Array.isArray(coursesResult.value?.data)
                        ? coursesResult.value.data
                        : [];
                    const detailedRowsRaw = detailedResult.status === 'fulfilled'
                        ? (Array.isArray(detailedResult.value?.data)
                            ? detailedResult.value.data
                            : detailedResult.value?.data?.courses || [])
                        : [];

                    const detailedByCourseId = new Map();
                    detailedRowsRaw.forEach((row) => {
                        const key = String(row.course_id || row.courseId || '');
                        if (key && !detailedByCourseId.has(key)) {
                            detailedByCourseId.set(key, row);
                        }
                    });

                    const mappedFromCourses = courseRows.map((row) => {
                        const detail = detailedByCourseId.get(String(row.id || '')) || {};
                        return {
                            courseId: row.id,
                            courseCode: row.code ?? row.course_code ?? row.courseCode ?? '',
                            courseName: safeText(row.name ?? row.course_name ?? row.courseName),
                            programId: row.programId ?? row.program_id ?? detail.program_id ?? detail.programId,
                            programName: safeText(row.programName ?? row.program_name ?? detail.program_name ?? detail.programName),
                            facultyId: detail.faculty_id ?? detail.facultyId,
                            facultyName: safeText(detail.faculty_name ?? detail.facultyName),
                            educationTypeId: detail.education_type_id ?? detail.educationTypeId,
                            educationTypeName: safeText(detail.education_type_name ?? detail.educationTypeName),
                            semester: row.semester ?? detail.semester,
                            academicYear: row.academicYear ?? row.academic_year ?? detail.academic_year ?? detail.academicYear,
                        };
                    }).filter(it => it.courseId);

                    if (mappedFromCourses.length > 0) {
                        setAssignedCourses(mappedFromCourses);
                        return;
                    }

                    // Ders listesi tek kaynak olan /api/akademisyen/courses'tan gelir.
                    // Detay endpointine fallback yapmak eski yıl atamalarını yeni yıla sızdırıyordu.
                    if (courseRows.length === 0) {
                        setAssignedCourses([]);
                        return;
                    }

                    // Fallback: detay endpointten eski akışla doldur
                    const mappedFromDetailed = detailedRowsRaw.map(row => ({
                        courseId: row.course_id || row.courseId,
                        courseCode: row.course_code ?? row.courseCode ?? row.code ?? '',
                        courseName: safeText(row.course_name || row.courseName),
                        programId: row.program_id || row.programId,
                        programName: safeText(row.program_name || row.programName),
                        facultyId: row.faculty_id || row.facultyId,
                        facultyName: safeText(row.faculty_name || row.facultyName),
                        educationTypeId: row.education_type_id || row.educationTypeId,
                        educationTypeName: safeText(row.education_type_name || row.educationTypeName),
                        semester: row.semester,
                        academicYear: row.academic_year || row.academicYear,
                    })).filter(it => it.courseId);

                    setAssignedCourses(mappedFromDetailed);
                })
                .catch(() => setAssignedCourses([]));
        }
    }, [akademisyenId, safeText]);

    // Fetch functions with useCallback
    const fetchEducationTypes = useCallback(async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.EDUCATION_TYPES);
            let educationTypes = response.data;
            
            // API'den gelen veriyi kontrol et ve düzelt
            if (Array.isArray(educationTypes)) {
                educationTypes = educationTypes.map(type => {
                    // Eğer type bir string ise (URL olabilir), onu atla
                    if (typeof type === 'string') {
                        return null;
                    }
                    // Eğer type objesi ama name yoksa, id'yi name olarak kullan
                    if (type && !type.name && type.id) {
                        return { ...type, name: `Eğitim Türü ${type.id}` };
                    }
                    return type;
                }).filter(type => type !== null);
            }
            
            if (allowedEducationTypes.length > 0) {
                educationTypes = educationTypes.filter(type => allowedEducationTypes.map(String).includes(String(type.id)));
            }
            setEducationTypes(educationTypes);
            
            // Otomatik seçimi kaldır - kullanıcı manuel seçim yapacak
        } catch (error) {
            // Hata sessizce yoksayılıyor
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedEducationTypes]);

    const fetchFaculties = useCallback(async (educationTypeId) => {
        try {
            // Backend param adı: educationTypeId
            const response = await axios.get(`${API_ENDPOINTS.FACULTIES}?educationTypeId=${educationTypeId}`);
            let faculties = response.data;
            
            // API'den gelen veriyi kontrol et ve düzelt
            if (Array.isArray(faculties)) {
                faculties = faculties.map(faculty => {
                    // Eğer faculty bir string ise (URL olabilir), onu atla
                    if (typeof faculty === 'string') {
                        return null;
                    }
                    // Eğer faculty objesi ama name yoksa, id'yi name olarak kullan
                    if (faculty && !faculty.name && faculty.id) {
                        return { ...faculty, name: `Fakülte ${faculty.id}` };
                    }
                    return faculty;
                }).filter(faculty => faculty !== null);
            }
            
            if (allowedFaculties.length > 0) {
                faculties = faculties.filter(faculty => allowedFaculties.map(String).includes(String(faculty.id)));
            }
            setFaculties(faculties);
            
            // Otomatik seçimi kaldır - kullanıcı manuel seçim yapacak
        } catch (error) {
            // Hata sessizce yoksayılıyor
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedFaculties]);

    const fetchPrograms = useCallback(async (facultyId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/programs?facultyId=${facultyId}`);
            let programs = response.data;
            
            // API'den gelen veriyi kontrol et ve düzelt
            if (Array.isArray(programs)) {
                programs = programs.map(program => {
                    if (typeof program === 'string') {
                        return null;
                    }
                    if (program && !program.name && program.id) {
                        return { ...program, name: `Program ${program.id}` };
                    }
                    return program;
                }).filter(program => program !== null);
            }
            
            if (allowedPrograms.length > 0) {
                programs = programs.filter(program => allowedPrograms.map(String).includes(String(program.id)));
            }
            setPrograms(programs);
            
            // Otomatik seçimi kaldır - kullanıcı manuel seçim yapacak
        } catch (error) {
            // Hata sessizce yoksayılıyor
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedPrograms]);

    const fetchCourses = useCallback(async (programId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/courses?programId=${programId}`);
            let courses = response.data;
            
            // API'den gelen veriyi kontrol et ve düzelt
            if (Array.isArray(courses)) {
                courses = courses.map(course => {
                    // Eğer course bir string ise (URL olabilir), onu atla
                    if (typeof course === 'string') {
                        return null;
                    }
                    // Eğer course objesi ama name yoksa, id'yi name olarak kullan
                    if (course && !course.name && course.id) {
                        return { ...course, name: `Ders ${course.id}` };
                    }
                    return course;
                }).filter(course => course !== null);
            }
            
            if (akademisyenId || allowedCourses.length > 0) {
                courses = courses.filter(course => allowedCourses.map(String).includes(String(course.id)));
            }
            setCourses(courses);
            
            // Otomatik seçimi kaldır - kullanıcı manuel seçim yapacak
        } catch (error) {
            // Hata sessizce yoksayılıyor
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedCourses]);

    const fetchExamTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/exam-types`);
            // Response'a 'Bütünleme'yi ekle (benzersiz tut)
            const raw = Array.isArray(response.data) ? response.data : [];
            const names = new Set(raw.map(x => (x?.type || '').toString().toLowerCase()));
            if (!names.has('bütünleme')) {
                raw.push({ type: 'bütünleme' });
            }
            setExamTypes(raw);
        } catch (error) {
            // Hata sessizce yoksayılıyor
        }
    }, []);

    const fetchProgramOutcomes = useCallback(async (programId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/program-outcomes/${programId}`);
            if (response.data && response.data.outcomes) {
                setProgramOutcomes(response.data.outcomes);
            } else {
                setProgramOutcomes([]);
            }
        } catch (error) {
            setProgramOutcomes([]);
        }
    }, []);

    const fetchCourseOutcomes = useCallback(async (courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/course-outcomes/${courseId}`);
            if (response.data && response.data.learning) {
                setCourseOutcomes(response.data.learning);
            } else {
                setCourseOutcomes([]);
            }
        } catch (error) {
            setCourseOutcomes([]);
        }
    }, []);

    const fetchQuestions = useCallback(async (courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-questions-by-course/${courseId}`);
            setQuestions(response.data);
        } catch (error) {
            setQuestions([]);
        }
    }, []);

    const fetchOutcomeMappings = useCallback(async (programId, courseId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/get-outcome-mappings?program_id=${programId}&course_id=${courseId}`);
            setOutcomeMappings(response.data || {});
        } catch (error) {
            setOutcomeMappings({});
        }
    }, []);

    const fetchExamWeights = useCallback(async (programId, courseId) => {
        try {
            if (!programId || !courseId) return;

            const response = await axios.get(`${API_BASE_URL}/get-exam-weights/${programId}/${courseId}`);
            
            if (response.data && response.data.weights) {
                setExamWeights(response.data.weights);
            } else {
                setExamWeights([]);
            }
        } catch (error) {
            setExamWeights([]);
        }
    }, []);

    // Yüklenen dosyaları listele
    const fetchUploadedFiles = useCallback(async (programId, courseId) => {
        try {
            if (!programId || !courseId) return;
            const res = await axios.get(`${API_BASE_URL}/list-uploads`, {
                params: { program_id: programId, course_id: courseId }
            });
            setUploadedFiles(Array.isArray(res.data.files) ? res.data.files : []);
        } catch (e) {
            setUploadedFiles([]);
        }
    }, []);

    // Exam attempts çek
    const fetchExamAttempts = useCallback(async (courseId) => {
        try {
            if (!courseId) return;
            const res = await axios.get(API_ENDPOINTS.EXAM_ATTEMPTS(courseId));
            setExamAttempts(Array.isArray(res.data.attempts) ? res.data.attempts : []);
        } catch (e) {
            setExamAttempts([]);
        }
    }, []);

    // Ders listesi (öğrenci listesi) çek
    const fetchClassRoster = useCallback(async (programId, courseId) => {
        try {
            if (!courseId) return;
            const params = { course_id: courseId };
            if (programId) params.program_id = programId;
            const res = await axios.get(`${API_BASE_URL}/api/student-list`, { params });
            // Yeni API 'students' döndürüyor, eski API 'roster' döndürüyordu
            const data = res.data.students || res.data.roster || [];
            setClassRoster(Array.isArray(data) ? data : []);
        } catch (e) {
            setClassRoster([]);
        }
    }, []);

    // Öğrenci bazlı sınav yüzdeleri (vize/final) haritası
    const examPercentsByStudent = useMemo(() => {
        const map = {};
        const list = results?.students || [];
        list.forEach(st => {
            const studentNo = String(st.student_no || '').trim();
            const nameKey = String(st.student_name || '').trim().toLowerCase();
            const key = studentNo || nameKey;
            const et = canonExamType(st.exam_type);
            if (!map[key]) map[key] = {};

            // 1) Öncelik: backend'den gelen dc_percent
            const dcPercent = Number(st.dc_percent);
            if (Number.isFinite(dcPercent)) {
                map[key][et] = dcPercent;
                return;
            }

            // 2) Soru skorlarına göre toplam puan (varsa)
            const answers = st.answer_key || [];
            let earnedPoints = 0;
            let hasScore = false;
            for (let i = 0; i < answers.length; i += 1) {
                const ans = answers[i];
                const isUpperAlpha = ans && ans === ans.toUpperCase() && ans !== ans.toLowerCase();
                const qScore = Number(questions?.[i]?.score || 0);
                if (qScore > 0) hasScore = true;
                if (isUpperAlpha && qScore > 0) {
                    earnedPoints += qScore;
                }
            }
            if (hasScore) {
                map[key][et] = Number(earnedPoints.toFixed(2));
                return;
            }

            // 3) Fallback: yüzde hesapla
            const total = answers.length;
            const correct = answers.filter(ans => ans && ans === ans.toUpperCase() && ans !== ans.toLowerCase()).length;
            const percent = total > 0 ? Number(((correct / total) * 100).toFixed(2)) : 0;
            map[key][et] = percent;
        });
        return map;
    }, [results, canonExamType, questions]);

    // Prefetch precise per-exam scores for roster from backend (toplu endpoint, tek çağrı)
    useEffect(() => {
        const abortCtrl = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                setScoresReady(false);
                if (!selectedCourse || !Array.isArray(classRoster) || classRoster.length === 0) { setStudentScoresByKey({}); setScoresReady(true); return; }
                if (!Array.isArray(examWeights) || examWeights.length === 0) { setStudentScoresByKey({}); setScoresReady(true); return; }
                const payload = {
                    program_id: selectedProgram,
                    course_id: selectedCourse,
                    students: classRoster.map(r => ({
                        student_no: r.student_no,
                        student_name: (r.student_name || '').trim()
                    }))
                };
                const res = await fetch(API_ENDPOINTS.STUDENT_EXAM_SCORES_BATCH, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    signal: abortCtrl.signal
                });
                const data = await res.json();
                if (cancelled) return;
                if (!res.ok) {
                    setStudentScoresByKey({});
                    setScoresReady(true);
                    return;
                }
                const scores = data?.scores || {};
                const next = {};
                const canonMap = {};
                examWeights.forEach(w => {
                    const t = canonExamType(w.type || w.exam_type);
                    canonMap[String(w.id)] = t;
                });
                for (const [key, exams] of Object.entries(scores)) {
                    const targetKey = String(key || '').trim();
                    if (!targetKey) continue;
                    next[targetKey] = next[targetKey] || {};
                    Object.entries(exams || {}).forEach(([examId, val]) => {
                        const t = canonMap[examId];
                        if (!t) return;
                        const num = Number(val);
                        if (Number.isFinite(num)) next[targetKey][t] = num;
                    });

                    // Backend bazı kayıtlarda yalnızca dc_percent döndürüyor.
                    // Bu durumda en azından notun görünmesi için değeri tüm sınav tiplerine yansıt.
                    if (Object.keys(next[targetKey]).length === 0) {
                        const generic = Number(exams?.dc_percent);
                        if (Number.isFinite(generic)) {
                            examWeights.forEach((w) => {
                                const t = canonExamType(w.type || w.exam_type);
                                if (t) next[targetKey][t] = generic;
                            });
                        }
                    }
                }
                setStudentScoresByKey(next);
                setScoresReady(true);
            } catch (_) {
                if (!cancelled) {
                    setStudentScoresByKey({});
                    setScoresReady(true);
                }
            }
        })();
        return () => {
            cancelled = true;
            abortCtrl.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classRoster, examWeights, selectedCourse, selectedProgram, canonExamType]);

    // Sınav ağırlıkları map'i (tip -> ağırlık)
    const weightsByType = useMemo(() => {
        const w = {};
        (examWeights || []).forEach(x => {
            const t = canonExamType(x.type || x.exam_type);
            const wt = Number(x.weight || 0);
            if (t) w[t] = wt;
        });
        return w;
    }, [examWeights, canonExamType]);

    const calculateWeightedGeneral = useCallback((rec) => {
        const r = rec || {};
        // Final yerine bütünleme: büt notu sıfırdan büyükse final hariç tutulur, büt final ağırlığıyla çarpılır.
        const hasBut = typeof r['but'] === 'number' && r['but'] > 0;
        let total = 0;
        const finalWeight = weightsByType['final'] || 0;
        // Tanımlı sınav türleri: kayıtta yoksa 0 kabul et (yüklenmiş not olmayan sınav düşüşü).
        Object.keys(weightsByType || {}).forEach((t) => {
            const wt = weightsByType[t] || 0;
            const raw = r[t];
            const pts = Number(raw != null ? raw : 0);
            if (!Number.isFinite(pts)) return;
            if (hasBut && t === 'final') {
                return;
            }
            if (hasBut && t === 'but') {
                if (finalWeight > 0) {
                    total += pts * (finalWeight / 100);
                }
                return;
            }
            if (wt > 0) {
                total += pts * (wt / 100);
            }
        });
        return Number(total.toFixed(2));
    }, [weightsByType]);

    const gradeFromPercent = useCallback((p) => {
        // PDFExporter ile eşleşen eşikler: AA 90+, BA 85+, BB 80+, CB 75+, CC 60+, DC 55+, DD 50+, FD 40+, FF <40
        const x = Number(p || 0);
        if (x >= 90) return 'AA';
        if (x >= 85) return 'BA';
        if (x >= 80) return 'BB';
        if (x >= 75) return 'CB';
        if (x >= 60) return 'CC';
        if (x >= 55) return 'DC';
        if (x >= 50) return 'DD';
        if (x >= 40) return 'FD';
        return 'FF';
    }, []);

    // Harf notu görünümü için durumlar
    const gradeOrder = useMemo(() => ['AA', 'BA', 'BB', 'CB', 'CC', 'DC', 'DD', 'FD', 'FF'], []);
    const [activeGrade, setActiveGrade] = useState('');
    const [activeType, setActiveType] = useState('genel');
    const [isGradePanelOpen, setIsGradePanelOpen] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const gradePanelRef = useRef(null);
    const [panelMaxHeight, setPanelMaxHeight] = useState(0);

    const rosterWithGrades = useMemo(() => {
        if (!Array.isArray(classRoster) || classRoster.length === 0) return [];
        return classRoster.map((r) => {
            const rosterNo = String(r.student_no || '').trim();
            const rosterNameKey = (r.student_name || '').trim().toLowerCase();
            const key = rosterNo || rosterNameKey;
            const fetched = studentScoresByKey[key] || examPercentsByStudent[key] || {};
            const rec = scoresReady ? fetched : {};
            const genel = calculateWeightedGeneral(rec);
            const grade = gradeFromPercent(genel);
            return { roster: r, genel, grade };
        });
    }, [classRoster, studentScoresByKey, examPercentsByStudent, scoresReady, calculateWeightedGeneral, gradeFromPercent]);

    const groupedByGrade = useMemo(() => {
        const map = {};
        for (const item of rosterWithGrades) {
            const g = item.grade || 'FF';
            if (!map[g]) map[g] = [];
            map[g].push(item);
        }
        return map;
    }, [rosterWithGrades]);

    const buildListFor = useCallback((type, grade) => {
        if (type === 'genel') {
            return rosterWithGrades.filter(x => x.grade === grade).map(x => ({ roster: x.roster, value: x.genel }));
        }
        const list = [];
        if (!Array.isArray(classRoster) || classRoster.length === 0) return list;
        for (const r of classRoster) {
            const rosterNo = String(r.student_no || '').trim();
            const rosterNameKey = (r.student_name || '').trim().toLowerCase();
            const key = rosterNo || rosterNameKey;
            const fetched = studentScoresByKey[key] || examPercentsByStudent[key] || {};
            const rec = scoresReady ? fetched : {};
            if (!scoresReady) continue;
            if (rec[type] == null) continue;
            const val = Number(rec[type]);
            if (!Number.isFinite(val)) continue;
            const g = gradeFromPercent(val);
            if (g === grade) list.push({ roster: r, value: val });
        }
        return list;
    }, [classRoster, studentScoresByKey, examPercentsByStudent, scoresReady, rosterWithGrades, gradeFromPercent]);

    const handleCountClick = useCallback((type, grade, count) => {
        if (!count) return;
        const same = activeGrade === grade && activeType === type;
        const nextOpen = same ? !isGradePanelOpen : true;
        setActiveType(type);
        setActiveGrade(grade);
        setSelectedList(buildListFor(type, grade));
        setIsGradePanelOpen(nextOpen);
        // Yalnızca gerekirse, paneli görünür kılacak kadar kaydır
        setTimeout(() => {
            try {
                if (!gradePanelRef.current || !nextOpen) return;
                const rect = gradePanelRef.current.getBoundingClientRect();
                const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (fullyVisible) return;
                const offset = 120; // tablo başlığı görünür kalsın
                const targetY = Math.max(0, rect.top + window.scrollY - offset);
                window.scrollTo({ top: targetY, behavior: 'smooth' });
            } catch (_) {}
        }, 80);
    }, [activeGrade, activeType, isGradePanelOpen, buildListFor]);

    useEffect(() => {
        if (!isGradePanelOpen || !gradePanelRef.current) return;
        const el = gradePanelRef.current.querySelector('.grade-panel-content');
        if (el) setPanelMaxHeight(el.scrollHeight);
    }, [isGradePanelOpen, activeGrade, groupedByGrade]);

    // Harf dağılımı: Genel ve her sınav türü için sayım tablosu
    const examTypeOrder = useMemo(() => Array.from(new Set((examWeights || []).map(w => canonExamType(w.type || w.exam_type)))), [examWeights, canonExamType]);
    const examTypeLabels = useMemo(() => {
        const map = {};
        examTypeOrder.forEach(t => { map[t] = labelForExamType ? labelForExamType(t) : t; });
        return map;
    }, [examTypeOrder, labelForExamType]);

    const gradeDistribution = useMemo(() => {
        const initCounts = () => gradeOrder.reduce((acc, g) => { acc[g] = 0; return acc; }, {});
        const dist = { genel: initCounts() };
        examTypeOrder.forEach(t => { dist[t] = initCounts(); });

        if (!Array.isArray(classRoster) || classRoster.length === 0) return dist;

        for (const r of classRoster) {
            const rosterNo = String(r.student_no || '').trim();
            const rosterNameKey = (r.student_name || '').trim().toLowerCase();
            const key = rosterNo || rosterNameKey;
            const fetched = studentScoresByKey[key] || examPercentsByStudent[key] || {};
            const rec = scoresReady ? fetched : {};
            if (scoresReady) {
                const genel = calculateWeightedGeneral(rec);
                const g = gradeFromPercent(genel);
                if (g && dist.genel[g] != null) dist.genel[g] += 1;
            }
            // Her sınav türü için ayrı ayrı sayaç (not girilmemiş sınav türleri sayılmaz)
            if (!scoresReady) continue;
            for (const t of examTypeOrder) {
                if (rec[t] == null) continue;
                const val = Number(rec[t]);
                if (!Number.isFinite(val)) continue;
                const gg = gradeFromPercent(val);
                if (gg && dist[t] && dist[t][gg] != null) dist[t][gg] += 1;
            }
        }
        return dist;
    }, [classRoster, studentScoresByKey, examPercentsByStudent, scoresReady, calculateWeightedGeneral, gradeOrder, examTypeOrder, gradeFromPercent]);

    const totalsByType = useMemo(() => {
        const out = { genel: 0 };
        const sumAll = (obj) => Object.values(obj || {}).reduce((s, v) => s + Number(v || 0), 0);
        out.genel = sumAll(gradeDistribution.genel || {});
        examTypeOrder.forEach(t => { out[t] = sumAll(gradeDistribution[t] || {}); });
        return out;
    }, [gradeDistribution, examTypeOrder]);

    const barClassForType = useCallback((type) => {
        if (type === 'genel') return 'type-genel';
        const t = String(type || '').toLowerCase();
        if (t === 'vize') return 'type-vize';
        if (t === 'final') return 'type-final';
        if (t === 'but') return 'type-but';
        return 'type-other';
    }, []);

    // 0% kırmızı -> 100% mavi renk geçişi
    const colorForPercent = useCallback((percent) => {
        const p = Math.max(0, Math.min(100, Number(percent || 0)));
        // Hue: 0 (red) -> ~220 (blue)
        const hue = 0 + (220 * (p / 100));
        return `hsl(${hue}, 80%, 50%)`;
    }, []);

    const normalizeOutcomeToken = useCallback((value) => {
        return String(value ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c');
    }, []);

    const toOutcomeTokens = useCallback((raw) => {
        const arr = Array.isArray(raw) ? raw : (raw != null ? [raw] : []);
        const tokens = [];

        arr.forEach((item) => {
            if (item == null) return;

            if (typeof item === 'object') {
                const maybeVals = [item.id, item.number, item.code, item.value];
                maybeVals.forEach((v) => {
                    const t = normalizeOutcomeToken(v);
                    if (t) tokens.push(t);
                });
                return;
            }

            const t = normalizeOutcomeToken(item);
            if (t) tokens.push(t);
        });

        return tokens;
    }, [normalizeOutcomeToken]);

    const getOutcomeMatchTokens = useCallback((outcome, prefix) => {
        const out = [];
        const idTok = normalizeOutcomeToken(outcome?.id);
        const numTok = normalizeOutcomeToken(outcome?.number);
        const codeTok = normalizeOutcomeToken(outcome?.code);

        if (idTok) out.push(idTok);
        if (numTok) out.push(numTok);
        if (codeTok) out.push(codeTok);
        if (numTok && prefix) out.push(`${prefix}${numTok}`); // oc6 / pc3 gibi kodlar için

        return out;
    }, [normalizeOutcomeToken]);

    const questionHasOutcome = useCallback((question, outcome, type) => {
        const raw = type === 'program'
            ? (question?.program_outcomes || [])
            : (question?.topics || question?.learning_outcomes || []);
        const questionTokens = toOutcomeTokens(raw);
        if (questionTokens.length === 0) return false;

        const outcomeTokens = getOutcomeMatchTokens(outcome, type === 'program' ? 'pc' : 'oc');
        return outcomeTokens.some((t) => questionTokens.includes(t));
    }, [toOutcomeTokens, getOutcomeMatchTokens]);

    const getStudentFallbackRate = useCallback((studentNo, studentName) => {
        const key = String(studentNo || '').trim() || String(studentName || '').trim().toLowerCase();
        if (!key) return 0;

        const rec = studentScoresByKey[key] || examPercentsByStudent[key] || {};
        if (!rec || Object.keys(rec).length === 0) return 0;

        if (selectedExamTypeForAnalysis) {
            const t = canonExamType(selectedExamTypeForAnalysis);
            const v = Number(rec[t]);
            return Number.isFinite(v) ? v : 0;
        }

        return calculateWeightedGeneral(rec);
    }, [
        studentScoresByKey,
        examPercentsByStudent,
        selectedExamTypeForAnalysis,
        canonExamType,
        calculateWeightedGeneral
    ]);

    const getDbPercentMap = useCallback((idsRaw, percentsRaw) => {
        const ids = Array.isArray(idsRaw) ? idsRaw : [];
        const percents = Array.isArray(percentsRaw) ? percentsRaw : [];
        const out = {};
        ids.forEach((id, idx) => {
            const key = String(id);
            const val = Number(percents[idx]);
            if (!Number.isFinite(val)) return;
            out[key] = val;
        });
        return out;
    }, []);

    // Yüzdelere göre durum kategorisi (1-4)
    const categoryFromPercent = (p) => {
        const x = Number(p || 0);
        if (x >= 85) return { key: 4, label: 'Mükemmel' };
        if (x >= 70) return { key: 3, label: 'Ortalama Üstü' };
        if (x >= 55) return { key: 2, label: 'Yeterli' };
        if (x >= 40) return { key: 1, label: 'Geliştirilmeye açık' };
        return { key: 0, label: 'İyileştirilmeli' };
    };
   
    // Sınıf listesi yükleme işlemi
    const handleUploadClassList = useCallback(async () => {
        try {
            setUploadMessage('');
            if (!classListFile) {
                setUploadMessage('Lütfen bir dosya seçin (.xls veya .xlsx).');
                return;
            }
            if (!selectedCourse) {
                setUploadMessage('Lütfen önce ders seçin.');
                return;
            }
            setIsUploadingClassList(true);
            const formData = new FormData();
            formData.append('file', classListFile);
            if (selectedProgram) formData.append('program_id', selectedProgram);
            formData.append('course_id', selectedCourse);
            // Akademisyen bilgileri (varsa) ve bağlamı ekleyelim
            const akademisyenId = sessionStorage.getItem('akademisyen_id');
            const akademisyenName = sessionStorage.getItem('akademisyen_name') || localStorage.getItem('akademisyen_name') || '';
            formData.append('akademisyen_id', akademisyenId || '');
            formData.append('akademisyen_name', akademisyenName);
            formData.append('education_type_id', selectedEducationType || '');
            formData.append('faculty_id', selectedFaculty || '');
            const res = await axios.post(`${API_BASE_URL}/api/upload-class-roster`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const inserted = res.data?.inserted ?? 0;
            const skipped = res.data?.skipped ?? 0;
            if (inserted > 0) {
                setUploadMessage(`Dosya başarıyla yüklendi. ${inserted} öğrenci eklendi${skipped > 0 ? `, ${skipped} zaten mevcuttu` : ''}.`);
            } else if (skipped > 0) {
                setUploadMessage(`Öğrenciler zaten kayıtlı (${skipped} kayıt mevcut).`);
            } else {
                setUploadMessage('Dosya işlendi fakat hiç öğrenci eklenemedi. Excel formatını kontrol edin.');
            }
            setClassListFile(null);
            if (selectedProgram && selectedCourse) {
                await fetchUploadedFiles(selectedProgram, selectedCourse);
            }
        } catch (e) {
            setUploadMessage(`Yükleme sırasında hata: ${e.response?.data?.error || e.message}`);
        } finally {
            setIsUploadingClassList(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classListFile, selectedProgram, selectedCourse, selectedEducationType, selectedFaculty]);

    // Sınıf listesi silme fonksiyonu
    const [isDeletingClassList, setIsDeletingClassList] = useState(false);
    const handleDeleteClassList = useCallback(async () => {
        if (!selectedCourse) {
            setUploadMessage('Lütfen önce bir ders seçin.');
            return;
        }
        if (!window.confirm('Bu derse ait tüm öğrenci listesini silmek istediğinize emin misiniz?')) {
            return;
        }
        setIsDeletingClassList(true);
        setUploadMessage('');
        try {
            await axios.delete(`${API_BASE_URL}/api/student-list/${selectedCourse}`);
            setUploadMessage('Sınıf listesi başarıyla silindi.');
            setClassRoster([]);
        } catch (e) {
            setUploadMessage(`Silme sırasında hata: ${e.response?.data?.error || e.message}`);
        } finally {
            setIsDeletingClassList(false);
        }
    }, [selectedCourse]);

    const handleSearch = useCallback(async (courseId, programId, facultyId, educationTypeId) => {
        // Artık yalnızca ders seçimi yeterli; diğer filtreler opsiyonel
        if (!courseId) {
            setError('Lütfen bir ders seçin');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const url = `${API_BASE_URL}/api/previous-exam-results`;
            const params = { course_id: courseId };
            if (educationTypeId) params.education_type_id = educationTypeId;
            if (facultyId) params.faculty_id = facultyId;
            if (programId) params.program_id = programId;

            const [response, commonResponse] = await Promise.all([
                axios.get(url, { params }),
                axios.get(`${API_BASE_URL}/api/pre-midterm/term-final-class-breakdown?course_id=${courseId}`)
            ]);
            setResults(response.data);
            setCommonClassAnalysis(commonResponse.data);
        } catch (error) {
            setCommonClassAnalysis(null);
            setError(`Sınav sonuçları yüklenirken bir hata oluştu: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    // Veri yükleme
    useEffect(() => {
        if (allowedEducationTypes.length > 0) {
            fetchEducationTypes();
        }
        fetchExamTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedEducationTypes]);

    // Eğitim türü seçildiğinde fakülte seçimini göster ve odaklan
    useEffect(() => {
        if (selectedEducationType) {
            setShowFaculty(true);
            setTimeout(() => {
                if (facultySelectRef.current) {
                    facultySelectRef.current.focus();
                }
            }, 100);
        } else {
            setShowFaculty(false);
            setShowProgram(false);
            setShowCourse(false);
        }
    }, [selectedEducationType]);

    // Fakülte seçildiğinde program seçimini göster ve odaklan
    useEffect(() => {
        if (selectedFaculty) {
            setShowProgram(true);
            setTimeout(() => {
                if (programSelectRef.current) {
                    programSelectRef.current.focus();
                }
            }, 100);
        } else {
            setShowProgram(false);
            setShowCourse(false);
        }
    }, [selectedFaculty]);

    // Program seçildiğinde ders seçimini göster ve odaklan
    useEffect(() => {
        if (selectedProgram) {
            setShowCourse(true);
            setTimeout(() => {
                if (courseSelectRef.current) {
                    courseSelectRef.current.focus();
                }
            }, 100);
        } else {
            setShowCourse(false);
        }
    }, [selectedProgram]);

    useEffect(() => {
        if (selectedEducationType && allowedFaculties.length > 0) {
            fetchFaculties(selectedEducationType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEducationType, allowedFaculties]);

    useEffect(() => {
        if (selectedFaculty && allowedPrograms.length > 0) {
            fetchPrograms(selectedFaculty);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFaculty, allowedPrograms]);

    useEffect(() => {
        if (!selectedProgram || (akademisyenId && allowedCourses.length === 0)) {
            setCourses([]);
            return;
        }
        fetchCourses(selectedProgram);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProgram, allowedCourses]);

    useEffect(() => {
        if (selectedCourse && selectedProgram) {
            fetchCourseOutcomes(selectedCourse);
            fetchQuestions(selectedCourse);
            fetchExamWeights(selectedProgram, selectedCourse);
            fetchProgramOutcomes(selectedProgram);
            fetchOutcomeMappings(selectedProgram, selectedCourse);
            fetchUploadedFiles(selectedProgram, selectedCourse);
            fetchClassRoster(selectedProgram, selectedCourse);
            fetchExamAttempts(selectedCourse);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourse, selectedProgram]);
    
    // Ders seçildiğinde sonuçları getir (ayrı useEffect ile - sonsuz döngü önleme)
    useEffect(() => {
        if (selectedCourse && selectedProgram) {
            // Küçük bir gecikme ile handleSearch çağır (diğer state'lerin settle olması için)
            const timer = setTimeout(() => {
                handleSearch(selectedCourse, selectedProgram, selectedFaculty, selectedEducationType);
            }, 100);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourse]);

    // Analizleri filtrele (sadece seçili sınav türü için)
    const filteredResults = useMemo(() => {
        if (!selectedExamTypeForAnalysis || !results || !results.students) return results;
        const filtered = {
            ...results,
            students: results.students.filter(stu => {
                if (stu.exam_type) {
                    return canonExamType(stu.exam_type) === canonExamType(selectedExamTypeForAnalysis);
                }
                return true;
            })
        };
        return filtered;
    }, [results, selectedExamTypeForAnalysis, canonExamType]);

    // Roster satırına tıklayınca mevcut sonuçlardaki öğrenciye aç
    const handleRosterRowClick = useCallback((rosterItem) => {
        if (!rosterItem) return;
        // Modal açılırken varsayılan: Genel (hiçbir sınav seçili olmasın)
        setSelectedExamTypeForAnalysis('');
        setHighlightedWeightIndex(null);
        const rosterNo = String(rosterItem.student_no || '').trim();
        const rosterFullName = `${(rosterItem.first_name || '').trim()} ${(rosterItem.last_name || '').trim()}`.trim().toLowerCase();
        const students = filteredResults?.students || [];
        let matched = null;
        if (rosterNo) {
            matched = students.find(st => String(st.student_no || '').trim() === rosterNo);
        }
        if (!matched && rosterFullName) {
            matched = students.find(st => String(st.student_name || '').trim().toLowerCase() === rosterFullName);
        }
        if (matched) {
            setSelectedStudent(matched);
            setIsStudentDetailModalOpen(true);
        } else {
            toast('Bu öğrenci için henüz yüklenmiş sınav sonucu bulunmuyor.', { icon: 'ℹ️', duration: 3000 });
        }
    }, [filteredResults]);

    // Öntest verilerini StudentDetailModal formatına dönüştür
    const transformPretestToModalData = useCallback((student, attempt, questions) => {
        const studentInfo = [null, student.student_no, student.student_name, null, 'Öntest'];
        const dc = questions.filter(q => q.is_correct).length;
        const totalQ = questions.length;
        const totalPoints = questions.reduce((s, q) => s + (q.final_points || 0), 0);
        const dcPercent = attempt?.percentage || (totalQ > 0 ? (dc / totalQ * 100) : 0);

        const questionAnalysis = questions.map(q => ({
            answer: q.selected_label || 'Boş',
            isCorrect: q.is_correct,
            questionNumber: q.question_order,
            questionText: q.question_text,
            correctAnswer: q.correct_label || '-',
            points: q.final_points,
        }));

        // answersRaw: büyük harf=doğru, küçük harf=yanlış (modal mantığına uygun)
        const answersRaw = questions.map(q => {
            if (!q.selected_label) return null;
            return q.is_correct ? q.selected_label.toUpperCase() : q.selected_label.toLowerCase();
        });

        // ÖÇ bazlı grupla
        const loMap = {};
        questions.forEach((q, idx) => {
            (q.learning_outcomes || []).forEach(lo => {
                if (!loMap[lo.id]) loMap[lo.id] = { ...lo, code: `ÖÇ${lo.number}`, description: lo.name, questions: [] };
                loMap[lo.id].questions.push({ ...q, idx });
            });
        });
        const learningOutcomeResults = Object.values(loMap).sort((a, b) => a.number - b.number).map(lo => {
            const correct = lo.questions.filter(q => q.is_correct).length;
            const total = lo.questions.length;
            return {
                id: lo.id, number: lo.number, name: lo.name, description: lo.name, code: lo.code,
                correctCount: correct, totalCount: total,
                successRate: total > 0 ? parseFloat((correct / total * 100).toFixed(2)) : 0,
                relatedQuestions: lo.questions.map((q, i) => ({
                    questionNumber: i + 1, questionText: q.question_text,
                    studentAnswer: q.selected_label || 'Boş', isCorrect: q.is_correct,
                    correctAnswer: q.correct_label || '-', questionIndex: q.idx, questionScore: q.base_points,
                })),
            };
        });

        // PÇ bazlı grupla
        const poMap = {};
        questions.forEach((q, idx) => {
            (q.program_outcomes || []).forEach(po => {
                if (!poMap[po.id]) poMap[po.id] = { ...po, code: `PÇ${po.number}`, description: po.name, questions: [] };
                poMap[po.id].questions.push({ ...q, idx });
            });
        });
        const programOutcomeResults = Object.values(poMap).sort((a, b) => a.number - b.number).map(po => {
            const correct = po.questions.filter(q => q.is_correct).length;
            const total = po.questions.length;
            return {
                id: po.id, number: po.number, name: po.name, description: po.name, code: po.code,
                correctCount: correct, totalCount: total,
                successRate: total > 0 ? parseFloat((correct / total * 100).toFixed(2)) : 0,
                relatedQuestions: po.questions.map((q, i) => ({
                    questionNumber: i + 1, questionText: q.question_text,
                    studentAnswer: q.selected_label || 'Boş', isCorrect: q.is_correct,
                    correctAnswer: q.correct_label || '-', questionIndex: q.idx, questionScore: q.base_points,
                })),
            };
        });

        return { studentInfo, questionAnalysis, answersRaw, dc, dcPercent, totalPoints, learningOutcomeResults, programOutcomeResults };
    }, []);

    // Sınıf listesinden öğrenciye tıklayınca öntest analizini aç
    const handlePretestStudentClick = useCallback(async (student, attempt) => {
        const rosterNo = String(student.student_no || '').trim();
        const rosterName = String(student.student_name || '').trim().toLowerCase();
        const optikRecords = (results?.students || []).filter(st => {
            if (rosterNo && String(st.student_no || '').trim() === rosterNo) return true;
            if (rosterName && String(st.student_name || '').trim().toLowerCase() === rosterName) return true;
            return false;
        });
        const optikTypes = [...new Set(optikRecords.map(st => st.exam_type).filter(Boolean))];

        if (!attempt) {
            // Öntest yoksa optik verilerle modal aç; optik de yoksa studentScoresByKey'e bak
            if (optikTypes.length === 0) {
                const key = rosterNo || rosterName;
                const scoreRec = studentScoresByKey[key] || examPercentsByStudent[key] || {};
                const scoreTypes = Object.keys(scoreRec).filter(t => scoreRec[t] != null);
                if (scoreTypes.length > 0) {
                    const fakeRecords = scoreTypes.map(t => ({
                        student_no: student.student_no,
                        student_name: student.student_name,
                        exam_type: t,
                        answer_key: [],
                        learning_outcomes: [],
                        program_outcomes: [],
                        learning_percents: [],
                        program_percents: [],
                        dc_percent: scoreRec[t]
                    }));
                    const firstType = scoreTypes[0];
                    setPretestModal({
                        student, attempt: null,
                        questions: null, studentData: null, pretestStudentData: null,
                        optikRecords: fakeRecords,
                        allExamTypes: scoreTypes,
                        selectedType: firstType,
                        scoreRec
                    });
                    return;
                }
                return;
            }
            const firstType = optikTypes[0];
            const firstRecord = optikRecords.find(st => canonExamType(st.exam_type || '') === canonExamType(firstType));
            // Roster'dan gelen gerçek ismi kullan (student_answers'ta boş olabilir)
            const recordWithName = firstRecord
                ? { ...firstRecord, student_name: student.student_name || firstRecord.student_name }
                : null;
            const studentData = recordWithName ? prepareStudentDataForModal(recordWithName) : null;
            setPretestModal({
                student, attempt: null,
                questions: null, studentData, pretestStudentData: null,
                optikRecords: optikRecords.map(r => ({ ...r, student_name: student.student_name || r.student_name })),
                allExamTypes: optikTypes,
                selectedType: firstType
            });
            return;
        }

        const allExamTypes = ['Öntest', ...optikTypes];
        setPretestModalLoading(true);
        setPretestModal({ student, attempt, questions: null, studentData: null, pretestStudentData: null, optikRecords, allExamTypes, selectedType: '' });
        try {
            const url = API_ENDPOINTS.STUDENT_PRETEST_ANALYSIS(attempt.id, selectedCourse);
            const res = await axios.get(url);
            const qs = res.data.questions || [];
            const studentData = transformPretestToModalData(student, attempt, qs);
            setPretestModal(prev => ({ ...prev, questions: qs, studentData, pretestStudentData: studentData }));
        } catch (e) {
            setPretestModal(prev => ({ ...prev, questions: [], studentData: null }));
        } finally {
            setPretestModalLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCourse, transformPretestToModalData, results, canonExamType, studentScoresByKey, examPercentsByStudent]);

    // 2. calculateGeneralOutcomeRates
    const calculateGeneralOutcomeRates = (filteredResults) => {
        if (commonClassAnalysis) {
            const typeKey = canonExamType(selectedExamTypeForAnalysis);
            const averages = commonClassAnalysis.class_averages || {};
            const learning = typeKey
                ? (averages.per_type_learning_outcomes?.[typeKey] || [])
                : (averages.learning_outcomes || []);
            const program = typeKey
                ? (averages.per_type_program_outcomes?.[typeKey] || [])
                : (averages.program_outcomes || []);
            const convert = (item) => ({
                id: item.id,
                code: item.code,
                description: item.description,
                averageRate: Number(item.avg_pct || 0),
                studentCount: Number(item.student_count || 0)
            });
            return {
                learningRates: learning.map(convert),
                programRates: program.map(convert)
            };
        }
        // Ortak motor yüklenmediyse eski doğru-soru fallback'ine dönme.
        if (commonClassAnalysis === null) return { learningRates: [], programRates: [] };
        if (!filteredResults || !filteredResults.students) return { learningRates: [], programRates: [] };

        // Sınıf listesi varsa onu baz al; yoksa sadece sınav yapanlar
        const useRoster = classRoster && classRoster.length > 0;
        const baseStudents = useRoster ? classRoster : filteredResults.students;
        const weightsMap = {};
        (examWeights || []).forEach((w) => {
            const key = canonExamType(w.type || w.exam_type);
            const wt = Number(w.weight || 0);
            if (key && Number.isFinite(wt) && wt > 0) {
                weightsMap[key] = wt;
            }
        });

        const allStudentOutcomes = baseStudents.map(baseStudent => {
            const bNo = String(baseStudent.student_no || '').trim();
            const bName = String(baseStudent.student_name || '').trim().toLowerCase();
            const studentExamRecords = (filteredResults.students || []).filter(s => {
                if (bNo && String(s.student_no || '').trim() === bNo) return true;
                if (bName && String(s.student_name || '').trim().toLowerCase() === bName) return true;
                return false;
            });
            const fallbackRate = getStudentFallbackRate(baseStudent.student_no, baseStudent.student_name);
            const weightedLearningById = {};
            const weightedProgramById = {};

            studentExamRecords.forEach((examRecord) => {
                const examTypeKey = canonExamType(examRecord?.exam_type);
                const examWeight = Number(weightsMap[examTypeKey] || 0);
                if (!(examWeight > 0)) return;

                const dbLearningMap = getDbPercentMap(examRecord?.learning_outcomes, examRecord?.learning_percents);
                const dbProgramMap = getDbPercentMap(examRecord?.program_outcomes, examRecord?.program_percents);

                const hasDbLearning = Object.keys(dbLearningMap || {}).length > 0;
                const hasDbProgram = Object.keys(dbProgramMap || {}).length > 0;

                // Backend ÖÇ verisi yoksa answer_key + questions.topics ile hesapla
                const computedLearningMap = !hasDbLearning ? (() => {
                    const answers = examRecord?.answer_key || [];
                    const map = {};
                    (Array.isArray(courseOutcomes) ? courseOutcomes : []).forEach(outcome => {
                        const relatedIdxs = (questions || [])
                            .map((q, idx) => {
                                const loIds = q.topics || q.learning_outcomes || [];
                                return Array.isArray(loIds) && loIds.some(id => String(id) === String(outcome.id)) ? idx : -1;
                            })
                            .filter(idx => idx !== -1);
                        if (relatedIdxs.length === 0) return;
                        const relAns = relatedIdxs.map(i => answers[i]);
                        const correct = relatedIdxs.filter(i => { const ans = answers[i]; const correctAns = (questions[i]?.correct_answer || ''); return ans && correctAns && ans.toUpperCase() === correctAns.toUpperCase(); }).length;
			map[String(outcome.id)] = relatedIdxs.length > 0 ? (correct / relatedIdxs.length) * 100 : 0;
                    });
                    return map;
                })() : {};

                // Backend PÇ verisi yoksa outcomeMappings üzerinden hesapla
                const computedProgramMap = !hasDbProgram ? (() => {
                    const answers = examRecord?.answer_key || [];
                    const map = {};
                    (Array.isArray(programOutcomes) ? programOutcomes : []).forEach(outcome => {
                        const relatedIdxs = new Set();
                        (questions || []).forEach((q, idx) => {
                            const loIds = q.topics || q.learning_outcomes || [];
                            if (Array.isArray(loIds)) {
                                loIds.forEach(loId => {
                                    const mappedPcs = outcomeMappings[String(loId)] || [];
                                    if (mappedPcs.includes(outcome.id)) relatedIdxs.add(idx);
                                });
                            }
                        });
                        if (relatedIdxs.size === 0) return;
                        const idxArr = Array.from(relatedIdxs);
                        const relAns = idxArr.map(i => answers[i]);
                        const correct = idxArr.filter(i => { const ans = answers[i]; const correctAns = (questions[i]?.correct_answer || ''); return ans && correctAns && ans.toUpperCase() === correctAns.toUpperCase(); }).length;
			map[String(outcome.id)] = idxArr.length > 0 ? (correct / idxArr.length) * 100 : 0;
                    });
                    return map;
                })() : {};

                const effectiveLearningMap = hasDbLearning ? dbLearningMap : computedLearningMap;
                const effectiveProgramMap = hasDbProgram ? dbProgramMap : computedProgramMap;

                Object.entries(effectiveLearningMap || {}).forEach(([outcomeId, rate]) => {
                    const numRate = Number(rate);
                    if (!Number.isFinite(numRate)) return;
                    weightedLearningById[outcomeId] = (weightedLearningById[outcomeId] || 0) + (numRate * examWeight / 100);
                });
                Object.entries(effectiveProgramMap || {}).forEach(([outcomeId, rate]) => {
                    const numRate = Number(rate);
                    if (!Number.isFinite(numRate)) return;
                    weightedProgramById[outcomeId] = (weightedProgramById[outcomeId] || 0) + (numRate * examWeight / 100);
                });
            });
            
            // ÖÇ analizi - Backend mantığına uygun: hem topics hem learning_outcomes destekle
            const hasAnyLearningData = Object.keys(weightedLearningById).length > 0;
            const learningOutcomeResults = Array.isArray(courseOutcomes) ? courseOutcomes.map((outcome) => {
                const weightedRate = Number(weightedLearningById[String(outcome.id)]);
                const successRate = Number.isFinite(weightedRate) ? weightedRate : (hasAnyLearningData ? 0 : fallbackRate);

                return {
                    id: outcome.id,
                    code: outcome.code,
                    description: outcome.description,
                    correctCount: 0,
                    totalCount: 0,
                    successRate: Number(successRate.toFixed(2))
                };
            }) : [];

            // PÇ analizi - programOutcomes array kontrolü
            const hasAnyProgramData = Object.keys(weightedProgramById).length > 0;
            const programOutcomeResults = Array.isArray(programOutcomes) ? programOutcomes.map((outcome) => {
                const weightedRate = Number(weightedProgramById[String(outcome.id)]);
                const successRate = Number.isFinite(weightedRate) ? weightedRate : (hasAnyProgramData ? 0 : fallbackRate);
                
                return {
                    id: outcome.id,
                    code: outcome.code,
                    description: outcome.description,
                    correctCount: 0,
                    totalCount: 0,
                    successRate: Number(successRate.toFixed(2))
                };
            }) : [];

            return {
                learningOutcomes: learningOutcomeResults,
                programOutcomes: programOutcomeResults
            };
        });
        
        // ÖÇ genel oranları - courseOutcomes array kontrolü
        const totalBase = allStudentOutcomes.length; // sınıf listesi veya sınav yapanlar
        const generalLearningRates = Array.isArray(courseOutcomes) ? courseOutcomes.map(outcome => {
            const allRates = allStudentOutcomes.map(studentOutcome => {
                const outcomeData = studentOutcome.learningOutcomes.find(oc => oc.id === outcome.id);
                return outcomeData ? outcomeData.successRate : 0;
            });
            const averageRate = totalBase > 0
                ? allRates.reduce((sum, rate) => sum + rate, 0) / totalBase
                : 0;

            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                averageRate: Number(averageRate.toFixed(2)),
                studentCount: totalBase
            };
        }) : [];

        // PÇ genel oranları - programOutcomes array kontrolü
        const generalProgramRates = Array.isArray(programOutcomes) ? programOutcomes.map(outcome => {
            const allRates = allStudentOutcomes.map(studentOutcome => {
                const outcomeData = studentOutcome.programOutcomes.find(pc => pc.id === outcome.id);
                return outcomeData ? outcomeData.successRate : 0;
            });
            const averageRate = totalBase > 0
                ? allRates.reduce((sum, rate) => sum + rate, 0) / totalBase
                : 0;

            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                averageRate: Number(averageRate.toFixed(2)),
                studentCount: totalBase
            };
        }) : [];

        return {
            learningRates: generalLearningRates,
            programRates: generalProgramRates
        };
    };

    // 3. renderGeneralOutcomeTables
    const renderGeneralOutcomeTables = (filteredResults) => {
        if (!filteredResults || !filteredResults.students) {
            return null;
        }

        const generalRates = calculateGeneralOutcomeRates(filteredResults);

        return (
            <div className="previous-results-section">
                <div className="results-header">
                    <h3> Sınav Sonuçları Analizi</h3>
                    <div className="results-info">
                        <span>Toplam Öğrenci: <strong>{classRoster.length > 0 ? classRoster.length : filteredResults.students.length}</strong></span>
                        <span>Sınav Türü: <strong>{selectedExamTypeForAnalysis || 'Tümü'}</strong></span>
                    </div>
                </div>

                {/* Sınav türü filtresi (ağırlıklar) - şık chip bar */}
                {Array.isArray(examWeights) && examWeights.length > 0 && (
                    <div className="weights-filter">
                        <span className="filter-label">Filtre:</span>
                        <button
                            type="button"
                            className={`weight-chip ${!selectedExamTypeForAnalysis ? 'selected' : ''}`}
                            onClick={() => handleWeightRowClick(-1, '')}
                        >
                            Genel
                        </button>
                        {examWeights.map((w, i) => {
                            const t = w.type || w.exam_type;
                            const label = labelForExamType ? labelForExamType(t) : t;
                            return (
                                <button
                                    key={`${t}-${i}`}
                                    type="button"
                                    className={`weight-chip ${selectedExamTypeForAnalysis === canonExamType(t) ? 'selected' : ''}`}
                                    onClick={() => handleWeightRowClick(i, t)}
                                    title={`${label} • %${w.weight}`}
                                >
                                    <span className="chip-label">{label}</span>
                                    <span className="chip-percent">%{w.weight}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* ÖÇ ve PÇ tabloları */}
                <div className="summary-grid">
                    <div className="summary-table-container">
                        <h4>Genel Dersin öğrenme çıktıları (ÖÇ) Başarı Oranları</h4>
                        <div className="table-scroll">
                            <table className="student-outcome-summary-table outcome-rates-table">
                                <colgroup>
                                    <col className="col-outcome-code" />
                                    <col className="col-outcome-desc" />
                                    <col className="col-outcome-rate" />
                                    <col className="col-outcome-count" />
                                    <col className="col-outcome-status" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th scope="col">ÖÇ Kodu</th>
                                        <th scope="col">Açıklama</th>
                                        <th scope="col">Genel başarı</th>
                                        <th scope="col">Öğrenci</th>
                                        <th scope="col">Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(generalRates.learningRates) && generalRates.learningRates.map((outcome, index) => (
                                        <tr key={index}>
                                            <td><strong>{outcome.code || `ÖÇ${outcome.id}`}</strong></td>
                                            <td>{outcome.description}</td>
                                            <td>
                                                <span className={`badge ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                    %{outcome.averageRate}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="student-count">{outcome.studentCount}</span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const cat = categoryFromPercent(outcome.averageRate);
                                                    return (
                                                        <span className={`status level-${cat.key}`}>
                                                            {cat.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="summary-table-container">
                        <h4>Genel Program Çıktıları (PÇ) Başarı Oranları</h4>
                        <div className="table-scroll">
                            <table className="student-outcome-summary-table outcome-rates-table">
                                <colgroup>
                                    <col className="col-outcome-code" />
                                    <col className="col-outcome-desc" />
                                    <col className="col-outcome-rate" />
                                    <col className="col-outcome-count" />
                                    <col className="col-outcome-status" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th scope="col">PÇ Kodu</th>
                                        <th scope="col">Açıklama</th>
                                        <th scope="col">Genel başarı</th>
                                        <th scope="col">Öğrenci</th>
                                        <th scope="col">Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(generalRates.programRates) && generalRates.programRates.map((outcome, index) => (
                                        <tr key={index}>
                                            <td><strong>{outcome.code || `PÇ${outcome.id}`}</strong></td>
                                            <td>{outcome.description}</td>
                                            <td>
                                                <span className={`badge ${outcome.averageRate >= 60 ? 'success' : 'fail'}`}>
                                                    %{outcome.averageRate}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="student-count">{outcome.studentCount}</span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const cat = categoryFromPercent(outcome.averageRate);
                                                    return (
                                                        <span className={`status level-${cat.key}`}>
                                                            {cat.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Sınıf Listesi - tıklanabilir satırlarla detay modalı açar */}
                {classRoster.length > 0 && (
                    <div className="summary-table-container">
                        <h4>Sınıf Listesi ({classRoster.length} öğrenci)</h4>
                        <div className="table-scroll">
                            <table className="student-outcome-summary-table class-roster-main-table" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th className="roster-col-index">#</th>
                                        <th className="roster-col-student-no">Öğrenci No</th>
                                        <th className="roster-col-name">Adı Soyadı</th>
                                        <th className="roster-col-pretest">Öntest</th>
                                        {examTypeOrder.map(t => (
                                            <th key={t} className="roster-exam-col">{examTypeLabels[t]} ({weightsByType[t] != null ? `%${weightsByType[t]}` : '-'})</th>
                                        ))}
                                        <th className="roster-col-grade">Harf</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classRoster.map((student, idx) => {
                                        const rosterNo = String(student.student_no || '').trim();
                                        const rosterNameKey = (student.student_name || '').trim().toLowerCase();
                                        const key = rosterNo || rosterNameKey;
                                        const rec = scoresReady ? (studentScoresByKey[key] || examPercentsByStudent[key] || {}) : {};
                                        const genelPct = scoresReady ? calculateWeightedGeneral(rec) : null;
                                        const harfNotu = scoresReady && Number.isFinite(genelPct) ? gradeFromPercent(genelPct) : null;
                                        const studentAttempts = examAttempts.filter(a => String(a.student_no) === rosterNo);
                                        const attempt = studentAttempts[0] || null;
                                        const statusIcon = attempt
                                            ? attempt.status === 'Submitted' ? '✓'
                                            : attempt.status === 'InProgress' ? '⏳'
                                            : attempt.status
                                            : '-';
                                        const statusColor = attempt
                                            ? attempt.status === 'Submitted' ? '#38a169'
                                            : attempt.status === 'InProgress' ? '#d97706'
                                            : '#718096'
                                            : '#718096';
                                        return (
                                            <tr
                                                key={student.id || idx}
                                                onClick={() => handlePretestStudentClick(student, attempt)}
                                                style={{ cursor: 'pointer' }}
                                                title="Detay için tıklayın"
                                            >
                                                <td className="roster-col-index">{idx + 1}</td>
                                                <td className="roster-col-student-no">{student.student_no}</td>
                                                <td className="roster-col-name">{student.student_name}</td>
                                                <td className="roster-col-pretest" style={{ color: statusColor, fontWeight: attempt ? 700 : 400, fontSize: '16px' }}>{statusIcon}</td>
                                                {examTypeOrder.map(t => {
                                                    const score = rec[t];
                                                    return (
                                                        <td key={t} className="roster-exam-col">{formatRosterExamScoreDisplay(score, scoresReady)}</td>
                                                    );
                                                })}
                                                <td className="roster-col-grade">
                                                    {harfNotu ? (
                                                        <span className={`grade-badge grade-${harfNotu}`}>{harfNotu}</span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Tanımlı sınav türleri (ağırlık tablosundan)
    // eslint-disable-next-line no-unused-vars
    const examWeightTypes = Array.from(new Set(examWeights.map(w => w.type || w.exam_type)));

    // Tabloya tıklanınca analizler için sınav türü seç
    const handleWeightRowClick = (index, type) => {
        setHighlightedWeightIndex(index);
        setSelectedExamTypeForAnalysis(type ? canonExamType(type) : '');
    };

    

    // Öğrenci detay modalı için veri hazırlama
    const prepareStudentDataForModal = useCallback((student) => {
        if (!student || !questions) return null;

        const studentAnswers = student.answer_key || [];
        const totalQuestionCount = studentAnswers.length;
        const pointsPerQuestion = totalQuestionCount > 0 ? 100 / totalQuestionCount : 0;
        const dbLearningMap = getDbPercentMap(student.learning_outcomes, student.learning_percents);
        const dbProgramMap = getDbPercentMap(student.program_outcomes, student.program_percents);
        
        // Soru analizi hazırla
        const questionAnalysis = questions.map((question, index) => {
            const studentAnswer = studentAnswers[index] || 'Boş';
            const correctAnswer = question.correct_answer || '';
            const isCorrect = studentAnswer && studentAnswer.toUpperCase() === correctAnswer.toUpperCase();
            
            return {
                answer: studentAnswer,
                isCorrect: isCorrect,
                questionNumber: index + 1,
                questionText: question.text || `Soru ${index + 1}`,
                correctAnswer: correctAnswer,
                points: isCorrect ? pointsPerQuestion : 0
            };
        });

        // Genel istatistikler
        const correctAnswers = studentAnswers.filter(ans => ans && ans.toUpperCase() === ans && ans !== ans.toLowerCase()).length;
        const wrongAnswers = studentAnswers.filter(ans => ans && ans !== ans.toUpperCase() && ans !== ans.toLowerCase()).length;
        const emptyAnswers = totalQuestionCount - correctAnswers - wrongAnswers;
        const successRate = totalQuestionCount > 0 ? (correctAnswers / totalQuestionCount) * 100 : 0;
        const totalPoints = correctAnswers * pointsPerQuestion;

        // Dersin öğrenme çıktıları analizi - Backend mantığına uygun: hem topics hem learning_outcomes destekle
        const learningOutcomeResults = Array.isArray(courseOutcomes) ? courseOutcomes.map((outcome) => {
            const relatedQuestionIndexes = questions
                .map((q, idx) => (questionHasOutcome(q, outcome, 'learning') ? idx : -1))
                .filter(idx => idx !== -1);
            
            // İlgili soruların cevaplarını al
            const relatedAnswers = relatedQuestionIndexes.map(idx => studentAnswers[idx]);
            
            // Backend mantığı: büyük harf = doğru (isupper()), küçük harf = yanlış, boş = boş
            const correctCount = relatedAnswers.filter(ans => {
                if (!ans) return false; // Boş cevaplar doğru değil
                const ansStr = String(ans);
                return ansStr === ansStr.toUpperCase() && ansStr !== ansStr.toLowerCase();
            }).length;
            
            const totalCount = relatedAnswers.length;
            const fallbackRate = getStudentFallbackRate(student.student_no, student.student_name);
            const dbRate = dbLearningMap[String(outcome.id)];
            const successRate = Number.isFinite(dbRate)
                ? dbRate
                : (totalCount > 0
                ? (correctCount / totalCount) * 100
                : fallbackRate);
            
            const relatedQuestions = relatedQuestionIndexes.map((idx, qIdx) => {
                const question = questions[idx];
                const answer = studentAnswers[idx];
                const isCorrect = answer && answer.toUpperCase() === answer && answer !== answer.toLowerCase();
                
                return {
                    questionNumber: idx + 1,
                    questionText: question.text || `Soru ${idx + 1}`,
                    studentAnswer: answer || 'Boş',
                    isCorrect: isCorrect,
                    correctAnswer: question.correct_answer || 'N/A',
                    questionIndex: idx
                };
            });
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                correctCount,
                totalCount,
                successRate: Number(successRate.toFixed(2)),
                relatedQuestions
            };
        }) : [];

        // Program çıktıları analizi
        const programOutcomeResults = Array.isArray(programOutcomes) ? programOutcomes.map((outcome) => {
            const relatedQuestionIndexes = questions
                .map((q, idx) => (questionHasOutcome(q, outcome, 'program') ? idx : -1))
                .filter(idx => idx !== -1);
            
            const relatedAnswers = relatedQuestionIndexes.map(idx => studentAnswers[idx]);
            const correctCount = relatedAnswers.filter(ans => ans && ans.toUpperCase() === ans && ans !== ans.toLowerCase()).length;
            const totalCount = relatedAnswers.length;
            const fallbackRate = getStudentFallbackRate(student.student_no, student.student_name);
            const dbRate = dbProgramMap[String(outcome.id)];
            const successRate = Number.isFinite(dbRate)
                ? dbRate
                : (totalCount > 0
                ? (correctCount / totalCount) * 100
                : fallbackRate);
            
            const relatedQuestions = relatedQuestionIndexes.map((idx, qIdx) => {
                const question = questions[idx];
                const answer = studentAnswers[idx];
                const isCorrect = answer && answer.toUpperCase() === answer && answer !== answer.toLowerCase();
                
                return {
                    questionNumber: idx + 1,
                    questionText: question.text || `Soru ${idx + 1}`,
                    studentAnswer: answer || 'Boş',
                    isCorrect: isCorrect,
                    correctAnswer: question.correct_answer || 'N/A',
                    questionIndex: idx
                };
            });
            
            return {
                id: outcome.id,
                code: outcome.code,
                description: outcome.description,
                correctCount,
                totalCount,
                successRate: Number(successRate.toFixed(2)),
                relatedQuestions
            };
        }) : [];

        return {
            studentInfo: [
                null, // index 0
                student.student_no || 'N/A', // index 1
                student.student_name || 'N/A', // index 2
                null, // index 3
                student.exam_type || 'N/A' // index 4 (kitapçık yerine sınav türü)
            ],
            answersRaw: studentAnswers,
            questionAnalysis: questionAnalysis,
            dc: correctAnswers,
            yc: wrongAnswers,
            bos: emptyAnswers,
            dcPercent: successRate,
            totalPoints: totalPoints,
            totalQuestionCount: totalQuestionCount,
            pointsPerQuestion: pointsPerQuestion,
            learningOutcomeResults: learningOutcomeResults,
            programOutcomeResults: programOutcomeResults
        };
    }, [questions, courseOutcomes, programOutcomes, getDbPercentMap, getStudentFallbackRate, questionHasOutcome]);

    return (
        <div className="previous-exam-results">
            <div className="page-header">
                <h2>{t('examResults.title')}</h2>
                <p>Daha önce okunan sınav sonuçlarını görüntüleyin ve analiz edin</p>
            </div>

            {/* Yalnızca Ders seçimi (atanan dersler) — Öğrenci Analizi ile birebir aynı */}
            <div className="selectors single">
                <div className="course-selector">
                    <label htmlFor="assigned-course-select">Ders:</label>
                    <SearchableDropdown
                        value={selectedCourse || ''}
                        onChange={(val) => {
                            setSelectedCourse(val);
                            const item = uniqueAssignedCourses.find(c => String(c.courseId) === String(val));
                            if (item) {
                                setSelectedEducationType(item.educationTypeId || '');
                                setSelectedFaculty(item.facultyId || '');
                                setSelectedProgram(item.programId || '');
                            }
                        }}
                        placeholder="— Ders seçin —"
                        searchPlaceholder="Ders adı veya koduyla ara…"
                        ariaLabel="Ders"
                        options={uniqueAssignedCourses.map(c => ({
                            value: String(c.courseId),
                            label: formatCourseLabel(c),
                            searchText: `${c.courseCode || ''} ${c.courseName || ''}`,
                        }))}
                    />
                </div>
            </div>


            {/* Harf Notları görünümü: gruplara tıklandığında öğrenciler açılır */}
            {selectedCourse && (
                <div className="summary-table-container class-roster-section">
                    {/* Başlık isteme: metni kaldırdık */}
                    {!scoresReady ? (
                        <div className="no-roster">Notlar yükleniyor, lütfen bekleyin...</div>
                    ) : (!rosterWithGrades || rosterWithGrades.length === 0) ? (
                        <div className="no-roster">Bu ders için hesaplanmış not bulunamadı.</div>
                    ) : (
                        <>
                            {/* Harf Dağılımı Tablosu: Genel + sınav türleri */}
                            <div className="table-scroll" style={{ marginBottom: 10 }}>
                                <table className="student-outcome-summary-table">
                                    <thead>
                                        <tr>
                                            <th>Harf</th>
                                            <th>Genel</th>
                                            {examTypeOrder.map(t => (
                                                <th key={t}>{examTypeLabels[t]}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gradeOrder.map((g) => (
                                            <tr key={g}>
                                                <td><span className={`grade-badge grade-${g}`}>{g}</span></td>
                                                <td>
                                                    {(() => {
                                                        const count = gradeDistribution.genel[g] || 0;
                                                        const total = Math.max(1, totalsByType.genel || 1);
                                                        const pct = Math.min(100, Math.round((count / total) * 100));
                                                        return (
                                                            <button type="button" className="count-cell" onClick={() => handleCountClick('genel', g, count)} disabled={!count}>
                                                                <span className={`count-bar ${barClassForType('genel')}`} style={{ width: `${pct}%`, background: colorForPercent(pct) }}></span>
                                                                <span className="count-text">{count}</span>
                                                            </button>
                                                        );
                                                    })()}
                                                </td>
                                                {examTypeOrder.map(t => (
                                                    <td key={`${t}-${g}`}>
                                                        {(() => {
                                                            const count = (gradeDistribution[t] && gradeDistribution[t][g]) || 0;
                                                            const total = Math.max(1, totalsByType[t] || 1);
                                                            const pct = Math.min(100, Math.round((count / total) * 100));
                                                            return (
                                                                <button type="button" className="count-cell" onClick={() => handleCountClick(t, g, count)} disabled={!count}>
                                                                    <span className={`count-bar ${barClassForType(t)}`} style={{ width: `${pct}%`, background: colorForPercent(pct) }}></span>
                                                                    <span className="count-text">{count}</span>
                                                                </button>
                                                            );
                                                        })()}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div
                                ref={gradePanelRef}
                                className="grade-panel"
                                style={{ overflow: 'hidden', transition: 'max-height 300ms ease', maxHeight: isGradePanelOpen ? panelMaxHeight : 0 }}
                            >
                                {activeGrade && (
                                    <div className="grade-panel-content">
                                        <div style={{ padding: '8px 12px', fontWeight: 700 }}>
                                            — {activeType === 'genel' ? 'Genel' : (examTypeLabels[activeType] || activeType)} • {activeGrade} - {selectedList.length}
                                        </div>
                                        <div className="table-scroll">
                                            <table className="student-outcome-summary-table roster-table">
                                                <thead>
                                                    <tr>
                                                        <th>Öğrenci No</th>
                                                        <th>Adı Soyadı</th>
                                                        <th>{activeType === 'genel' ? 'Genel' : (examTypeLabels[activeType] || activeType)}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedList.map(({ roster, value }, idx) => {
                                                        const rosterNo = String(roster.student_no || '').trim();
                                                        const rosterName = (roster.student_name || [roster.first_name, roster.last_name].filter(Boolean).join(' ') || '').trim().toLowerCase();
                                                        const students = filteredResults?.students || [];
                                                        const hasOptikData = rosterNo
                                                            ? students.some(st => String(st.student_no || '').trim() === rosterNo)
                                                            : students.some(st => String(st.student_name || '').trim().toLowerCase() === rosterName);
                                                        return (
                                                            <tr
                                                                key={idx}
                                                                onClick={hasOptikData ? () => handleRosterRowClick(roster) : undefined}
                                                                style={{ cursor: hasOptikData ? 'pointer' : 'default' }}
                                                                title={hasOptikData ? 'Detayı görmek için tıklayın' : 'Optik form verisi yüklenmemiş'}
                                                            >
                                                                <td>{roster.student_no || ''}</td>
                                                                <td>{roster.student_name || [roster.first_name, roster.last_name].filter(Boolean).join(' ') || ''}</td>
                                                                <td>{Number(value).toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}




            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Sınav Ağırlıkları tablosunu ÖÇ/PÇ bölümlerinin altına taşıdık */}

            {/* PDF İndirme Butonu */}
            {!loading && !error && filteredResults && filteredResults.students && (
                <PDFExporter
                    filteredResults={results}
                    weights={examWeights}
                    selectedProgramId={selectedProgram}
                    selectedCourseId={selectedCourse}
                    courseOutcomes={courseOutcomes}
                    programOutcomes={programOutcomes}
                    questions={questions}
                    selectedExamTypeForAnalysis=""
                    selectedEducationType={selectedEducationType}
                    selectedFaculty={selectedFaculty}
                    selectedProgram={selectedProgram}
                    selectedCourse={selectedCourse}
                    educationTypes={educationTypes}
                    faculties={faculties}
                    programs={programs}
                    courses={courses}
                    classRoster={classRoster}
                    examTypeOrder={examTypeOrder}
                    examTypeLabels={examTypeLabels}
                    weightsByType={weightsByType}
                    studentScoresByKey={studentScoresByKey}
                    examPercentsByStudent={examPercentsByStudent}
                    scoresReady={scoresReady}
                    examAttempts={examAttempts}
                    generalRates={calculateGeneralOutcomeRates(filteredResults)}
                />
            )}

            {/* Analizler */}
            {loading && (
                <div className="loading-section">
                    <div className="loading-spinner"></div>
                    <p>Sınav sonuçları yükleniyor...</p>
                </div>
            )}
            
            {error && (
                <div className="error-section">
                    <div className="error-message">
                        {error}
                    </div>
                </div>
            )}
            
            {!loading && !error && renderGeneralOutcomeTables(filteredResults)}

            {/* Öğrenci Detay Modalı */}
            {selectedStudent && (
                <StudentDetailModal
                    isOpen={isStudentDetailModalOpen}
                    onClose={() => {
                        setIsStudentDetailModalOpen(false);
                        setSelectedStudent(null);
                    }}
                    studentData={prepareStudentDataForModal(selectedStudent)}
                    studentGeneral={(function() {
                        try {
                            // Genel not: roster tablosundaki hesaplamayla aynı şekilde hesapla
                            const rosterNo = String(selectedStudent.student_no || '').trim();
                            const rosterNameKey = String(selectedStudent.student_name || '').trim().toLowerCase();
                            const key = rosterNo || rosterNameKey;
                            const rec = examPercentsByStudent[key] || {};
                            return calculateWeightedGeneral(rec);
                        } catch (_) { return 0; }
                    })()}
                    courseOutcomes={courseOutcomes}
                    programOutcomes={programOutcomes}
                    questions={questions}
                    outcomeMappings={{}}
                    courseId={selectedCourse}
                    programId={selectedProgram}
                    examTypes={(() => {
                        try {
                            if (!results || !Array.isArray(results.students)) return [];
                            const stuNo = String(selectedStudent.student_no || '').trim();
                            const fullName = String(selectedStudent.student_name || '').trim().toLowerCase();
                            const taken = results.students.filter(st => {
                                const sNo = String(st.student_no || '').trim();
                                const sName = String(st.student_name || '').trim().toLowerCase();
                                return (stuNo && sNo === stuNo) || (!stuNo && fullName && sName === fullName);
                            }).map(st => st.exam_type).filter(Boolean);
                            return Array.from(new Set(taken));
                        } catch (_) { return []; }
                    })()}
                    examWeights={examWeights}
                    selectedExamType={selectedExamTypeForAnalysis}
                    onSelectExamType={(val) => {
                        // Modal seçiminde state'i ham label olarak tut (UI highlight doğru olsun),
                        // hesap/filtre tarafında canonExamType ile normalize ediyoruz.
                        setSelectedExamTypeForAnalysis(val || '');
                        const canonVal = val ? canonExamType(val) : '';
                        const idx = examWeights.findIndex(w => canonExamType(w.type || w.exam_type) === canonVal);
                        setHighlightedWeightIndex(idx >= 0 ? idx : null);
                        // Modal açıkken sınav türü değişirse aynı öğrencinin yeni sınav kaydını yükle
                        try {
                            if (selectedStudent && results && Array.isArray(results.students)) {
                                const targetPool = canonVal
                                    ? results.students.filter(st => canonExamType(st.exam_type) === canonVal)
                                    : results.students;
                                const stuNo = String(selectedStudent.student_no || '').trim();
                                const fullName = String(selectedStudent.student_name || '').trim().toLowerCase();
                                let match = null;
                                if (stuNo) {
                                    match = targetPool.find(st => String(st.student_no || '').trim() === stuNo);
                                }
                                if (!match && fullName) {
                                    match = targetPool.find(st => String(st.student_name || '').trim().toLowerCase() === fullName);
                                }
                                if (match) {
                                    setSelectedStudent(match);
                                }
                            }
                        } catch (_) {}
                    }}
                />
            )}


        {/* Öntest Analiz Modalı - StudentDetailModal ile aynı tasarım */}
        {pretestModal && pretestModal.studentData && (
            <StudentDetailModal
                isOpen={true}
                onClose={() => setPretestModal(null)}
                studentData={pretestModal.studentData}
                studentGeneral={0}
                courseOutcomes={courseOutcomes}
                programOutcomes={programOutcomes}
                questions={
                    pretestModal.selectedType === 'Öntest'
                        ? (pretestModal.questions || []).map(q => ({
                            score: q.base_points,
                            text: q.question_text,
                            correct_answer: q.correct_label,
                            learning_outcomes: (q.learning_outcomes || []).map(lo => lo.id),
                            program_outcomes: (q.program_outcomes || []).map(po => po.id),
                          }))
                        : questions
                }
                outcomeMappings={{}}
                courseId={selectedCourse}
                programId={selectedProgram}
                examTypes={(pretestModal.allExamTypes || []).filter(t => String(t || '').trim().toLowerCase() !== 'öntest')}
                selectedExamType={pretestModal.selectedType || ''}
                onSelectExamType={(val) => {
                    if (!val) {
                        setPretestModal(prev => ({ ...prev, studentData: prev.pretestStudentData, selectedType: '' }));
                    } else {
                        const targetType = canonExamType(val);
                        const match = (pretestModal.optikRecords || []).find(
                            st => canonExamType(st.exam_type || '') === targetType
                        );
                        if (match) {
                            const optikData = prepareStudentDataForModal(match);
                            setPretestModal(prev => ({ ...prev, studentData: optikData, selectedType: val }));
                        } else {
                            // Eşleşme yoksa bile seçimi görsel olarak güncelle
                            setPretestModal(prev => ({ ...prev, selectedType: val }));
                        }
                    }
                }}
                examWeights={examWeights}
            />
        )}
        </div>
    );
};

export default PreviousExamResults;
